import * as React from 'react'
import * as Lodash from 'lodash/fp'

import DI from '../app/di'

interface IContext {
  isAnyDirty: boolean
  isDirty: (id: any) => boolean
  defile: (id: any) => void
  clean: (id: any) => void
}

export const Context = React.createContext<IContext>({
  isAnyDirty: false,
  isDirty: (id: any) => false,
  defile: (id: any) => {},
  clean: (id: any) => {},
})

export type Props = {
  leavePromptMessage: string
  children: React.ReactNode
}

export const ContextProvider = (props: Props) => {
  const history = React.useMemo(() => DI.get('history'), [])
  const [dirty, setDirty] = React.useState([] as any[])
  const [unblock, setUnblock] = React.useState<Function | null>(null)

  const isDirty = dirty.length > 0

  const cleanAll = React.useCallback(() => {
    setDirty([])
  }, [])

  const isDirtyFor = React.useCallback(
    (id: any) => {
      return dirty.includes(id)
    },
    [dirty],
  )

  const defile = React.useCallback((id: any) => {
    setDirty((dirty) => {
      if (!dirty.includes(id)) {
        return Lodash.uniq([...dirty, id])
      }
      return dirty
    })
  }, [])

  const clean = React.useCallback((id: any) => {
    setDirty((dirty) => {
      if (dirty.includes(id)) {
        return Lodash.filter((x) => x !== id, dirty)
      }
      return dirty
    })
  }, [])

  React.useEffect(() => {
    if (unblock && !isDirty) {
      unblock()
    }

    window.onbeforeunload = function () {
      return isDirty ? props.leavePromptMessage : null
    }

    if (isDirty) {
      if (unblock) {
        unblock()
      }

      // history v5 changed the block API: the blocker receives a transition
      // object and we show the confirmation ourselves, then retry to proceed.
      const newUnblock = history.block((tx: any) => {
        if (window.confirm(props.leavePromptMessage)) {
          newUnblock()
          tx.retry()
        }
      })

      // TODO: Comment why function form is needed here
      setUnblock((_) => newUnblock)
    }
  }, [isDirty]) // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = {
    isAnyDirty: isDirty,
    isDirty: isDirtyFor,
    cleanAll,
    clean,
    defile,
  }
  return (
    <Context.Provider value={contextValue}>{props.children}</Context.Provider>
  )
}

export const ContextConsumer = Context.Consumer

export const useDirtySanchez = () => {
  return React.useContext(Context)
}
